import { useTranslation } from 'react-i18next';
import { WidgetLoader } from 'datastake-daf/dist/components';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import Widget from '../../widget';
import { Style } from './style';
import ItemToFocus from '../../components/itemToFocust';
import { AnalysisService } from '../../../../../services';
import { RISK_CATEGORIES, RISK_TYPES } from '../../../../../config';
import { getRiskCategories } from '../../../../../helpers/dataMapping/scl';
import InformationAvailability from '../../components/informationAvailability';
import RiskProfileIcons from '../../../../../screens/View/templates/components/risk/riskProfileIcons';

const _default = {
  riskProfile: [
    {
      "item": "Legality\n\n",
      "Current": 0,
      "Baseline": 0,
    },
    {
      "item": "Human Rights",
      "Current": 0,
      "Baseline": 0,
    },
    {
      "item": "Conflict &\n Security",
      "Current": 0,
      "Baseline": 0,
    },
    {
      "item": "Environment &\n Natural Resources",
      "Current": 0,
      "Baseline": 0,
    },
    {
      "item": "Health & Safety",
      "Current": 0,
      "Baseline": 0,
    },
    {
      "item": "Community &\n Society",
      "Current": 0,
      "Baseline": 0,
    }
  ],
  completion: {},
};

const LocationAnalysis = ({
  data: options = [],
  selectedProject,
  showData,
}) => {
  const { t } = useTranslation();
  const [activeLocation, setActiveLocation] = useState(null);
  const [data, setData] = useState(_default);
  const [loading, setLoading] = useState(false);
  const contRef = useRef();

  const _fetch = async () => {
    setLoading(true);
    try {
      const data = await AnalysisService.getAnalysis(activeLocation, { project: selectedProject });

      let riskProfile;
      let riskCategories = 0;

      if (data.data.riskByCategory) {
        const _data = getRiskCategories(data.data);
        const values = Object.entries(_data).sort((a, b) => a[0] - b[0]).map((val) => val[1]);
        const keys = [
          "legality",
          "humanRights",
          "conflictSecurity",
          "environment",
          "healthSafety",
          "communitySociety",
          "date",
        ]
        const first = {
          legality: 0,
          humanRights: 0,
          conflictSecurity: 0,
          environment: 0,
          healthSafety: 0,
          communitySociety: 0,
          date: 0,
        };


        values.forEach((val) => {
          keys.forEach((k) => {
            if (!first[k] && val[k]) {
              first[k] = val[k];
            }
          });
        });

        const second = values[values.length - 1];


        riskProfile = [
          {
            "item": "Legality\n\n",
            'Baseline': first?.legality || 0,
            'Current': second?.legality || 0,
          },
          {
            "item": "Human Rights",
            'Baseline': first?.humanRights || 0,
            'Current': second?.humanRights || 0,
          },
          {
            "item": "Conflict &\n Security",
            'Baseline': first?.conflictSecurity || 0,
            'Current': second?.conflictSecurity || 0,
          },
          {
            "item": "Environment &\n Natural Resources",
            'Baseline': first?.environment || 0,
            'Current': second?.environment || 0,
          },
          {
            "item": "Health & Safety",
            'Baseline': first?.healthSafety || 0,
            'Current': second?.healthSafety || 0,
          },
          {
            "item": "Community &\n Society",
            'Baseline': first?.communitySociety || 0,
            'Current': second?.communitySociety || 0,
          }
        ];

        riskProfile.forEach((risk) => {
          if (risk.Current || risk.Baseline) {
            riskCategories += 1;
          }
        });
      }

      setData({
        riskProfile,
        monitoredRisks: data.data.subjectDetails.monitoredRisks || 0,
        sources:
          (data?.data?.sources?.featuredData || []).length
          + (data?.data?.sources?.otherInformationSources || []).length
          + (data?.data?.sources?.supplyChainActor
            || []).length,
        riskScore: Number(data.data?.subjectDetails?.riskScore).toFixed(1),
        completion: data.data.progressPerTime || [],
        riskCategories,
      })
    } catch (err) {
      console.log(err)
    }

    setLoading(false);
  };

  useEffect(() => {
    if (activeLocation) {
      _fetch();
    }
  }, [activeLocation]);

  useEffect(() => {
    if (options.length && !activeLocation) {
      setActiveLocation(options[0].datastakeId)
    }
  }, [options]);

  const roundScore = Math.round(data?.riskScore);
  const cont = contRef.current;

  const _riskProfile = useMemo(() => {
    if (data?.riskProfile) {
      const _current = t('Current');
      const _baseline = t('Baseline');
      const _new = data?.riskProfile.map((d) => ({
        item: d.item,
        [_current]: d.Current,
        [_baseline]: d.Baseline,
      }));

      return (
        <RiskProfileIcons
          data={_new}
          cont={cont}
          defaultFields={[_current, _baseline]}
        />
      )
    }

  }, [data, t]);

  return (
    <Widget title={t('Supply Chain Locations')} className="no-px-body">
      {loading && <WidgetLoader background='rgba(255, 255, 255, 0.8)' />}
      <Style className="analysis-container" ref={contRef}>
        {(showData) ? (
          <>
            <ItemToFocus
              selectedItem={activeLocation}
              items={options}
              t={t}
              onChange={(val) => {
                setActiveLocation(val);
              }}
            />
            <div className="info w-p">
              <div className="info-row">
                <div className="label">
                  <p>{t('Aggregate Risk')}</p>
                </div>
                <div className={`value tag tag-${roundScore}`}>
                  <p>
                    {data?.riskScore || '0.0'}
                  </p>
                </div>
              </div>

              <div className="info-row">
                <div className="label">
                  <p>{t('Information Sources')}</p>
                </div>
                <div className="value">
                  <p>{data?.sources || 0}</p>
                </div>
              </div>

              <div className="info-row">
                <div className="label">
                  <p>{t('Risk Categories')}</p>
                </div>
                <div className="value">
                  <p>{data?.riskCategories || 0} / {RISK_CATEGORIES}</p>
                </div>
              </div>

              <div className="info-row">
                <div className="label">
                  <p>{t('Risk Types')}</p>
                </div>
                <div className="value">
                  <p>{data?.monitoredRisks || 0} / {RISK_TYPES}</p>
                </div>
              </div>
            </div>
            <h3 className="w-p">{t('Risk Profile')}</h3>
            <div className="widget-items" style={{ marginTop: -20 }}>
              <div style={{ width: 300 }}>
                {_riskProfile}
              </div>
            </div>
            {data?.completion && (
              <InformationAvailability
                t={t}
                completion={data?.completion}
              />
            )}
          </>
        ) : null}
      </Style>
    </Widget>
  )
};

export default LocationAnalysis;
